import { Component, Input, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BookingAccessService } from '../../_services/booking-access/booking-access.service';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { BookingAccessFeature } from '../../_constants/booking-access';
import { BookingAccess, BookingAccessPerson, NamedBookingGrant } from '../../_models/maintenance/booking-access.model';

/**
 * Who can see this booking, on booking setup: the client's users set to Every booking at this client (WholeClient, read-only), and the
 * people named on the booking, with Add person and Remove. Shown only while the clientusers.booking-grants switch is
 * on and the user holds the BookingSetupView role feature; the API checks both again. Standalone, so the
 * module-declared booking setup dialog hosts it with ngComponentOutlet.
 */
@Component({
  selector: 'who-can-see-booking',
  standalone: true,
  imports: [FormsModule, DatePipe],
  templateUrl: './who-can-see-booking.component.html',
  // .field__control's unlayered width: 100% beats a Tailwind width utility, so the search box's flexible width is set
  // here: it grows to fill the row beside Search, and the row wraps only when even its minimum will not fit.
  styles: [`
    .who-can-see__search-input {
      flex: 1 1 10rem;
      width: auto;
      min-width: 0;
    }
  `],
})
export class WhoCanSeeBookingComponent implements OnInit {
  @Input() placementId: number | string;

  public visible = false;
  public access: BookingAccess | null = null;
  public searchTerm = '';
  public people: BookingAccessPerson[] = [];
  public searched = false;
  public selected: BookingAccessPerson | null = null;
  public endDate = '';
  public message = '';

  /** Today as yyyy-MM-dd, the earliest end date the date box offers. */
  public readonly today = new Date().toISOString().substring(0, 10);

  constructor(
    private bookingAccess: BookingAccessService,
    private featureSwitches: FeatureSwitchService,
    private featureHelper: FeatureHelperService,
  ) {}

  ngOnInit(): void {
    if (!this.featureHelper.hasFeature(BookingAccessFeature)) {
      return;
    }
    this.featureSwitches.isEnabled(FeatureSwitchKeys.clientUsersBookingGrants).subscribe(on => {
      this.visible = on;
      if (on) {
        this.load();
      }
    });
  }

  public get liveNamed(): NamedBookingGrant[] {
    return (this.access?.named ?? []).filter(n => n.isLive);
  }

  public get pastNamed(): NamedBookingGrant[] {
    return (this.access?.named ?? []).filter(n => !n.isLive);
  }

  public load(): void {
    this.bookingAccess.getBookingAccess(+this.placementId).subscribe({
      next: access => this.access = access,
      error: () => this.message = 'Could not load who can see this booking. Please try again.',
    });
  }

  public search(): void {
    const term = (this.searchTerm ?? '').trim();
    this.selected = null;
    if (term.length < 2) {
      this.people = [];
      this.searched = false;
      this.message = 'Type at least 2 letters of a name or email address.';
      return;
    }
    this.message = '';
    this.bookingAccess.searchPeople(term).subscribe({
      next: people => {
        this.people = people;
        this.searched = true;
      },
      error: () => this.message = 'Could not search for people. Please try again.',
    });
  }

  public add(): void {
    if (!this.selected) {
      this.message = 'Choose a person to add.';
      return;
    }
    this.message = '';
    this.bookingAccess.addPerson(+this.placementId, this.selected.userId, this.endDate || null).subscribe({
      next: () => {
        this.selected = null;
        this.people = [];
        this.searched = false;
        this.searchTerm = '';
        this.endDate = '';
        this.load();
      },
      error: err => this.message = err?.message || 'Could not add this person. Please try again.',
    });
  }

  public remove(grant: NamedBookingGrant): void {
    this.message = '';
    this.bookingAccess.removePerson(+this.placementId, grant.userId, grant.id).subscribe({
      next: () => this.load(),
      error: err => this.message = err?.message || 'Could not remove this person. Please try again.',
    });
  }
}
